import axios, { CancelTokenSource } from 'axios';
import * as React from 'react';
import { refreshPatientData } from 'services/patientServices';
import { PatientDto } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';

const cancelTokenSource = axios.CancelToken.source();

interface State {
  currentPatient?: PatientDto,
  changePatient: (patient?: PatientDto) => void,
  onPatientRefresh: (patient?: PatientDto) => void,
  patientLoading: boolean,
  cancelTokenSource: CancelTokenSource
}

interface Props {
  children: JSX.Element | Array<JSX.Element>
}

export const PatientContext = React.createContext<State>({
  changePatient: () => {},
  onPatientRefresh: () => {},
  patientLoading: false,
  cancelTokenSource
});

export class PatientProvider extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);

    this.state = {
      currentPatient: undefined,
      changePatient: this.changePatient,
      onPatientRefresh: this.onPatientRefresh,
      patientLoading: false,
      cancelTokenSource
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    const {currentPatient} = this.state;

    if (prevState.currentPatient !== currentPatient) {
      if (!currentPatient) {
        this.setState({
          patientLoading: true
        })
      } else {
        this.setState({
          patientLoading: false
        })
      }
    }
  }

  onPatientRefresh = (patient?: PatientDto) => {
    const {cancelTokenSource} = this.state;

    this.setState({
      currentPatient: undefined,
      patientLoading: true
    });

    const onFinally = () => {
      this.setState({
        patientLoading: false
      })
    };

    if (patient) {
      refreshPatientData(cancelTokenSource, patient.id)
          .then(response => {
            this.setState({
              currentPatient: response
            })
          })
          .catch(noop)
          .finally(onFinally)
    }
  };

  changePatient = (patient?: PatientDto) => {
    this.setState({currentPatient: patient});
  };

  render() {
    return (
        <PatientContext.Provider value={this.state}>
          {this.props.children}
        </PatientContext.Provider>
    );
  }
}

export const PatientConsumer = PatientContext.Consumer;

export interface PatientConsumerRenderProps extends State {
}

export const withPatientContext = (Component: typeof React.Component | React.FunctionComponent<any>) => {
  return (props: any) => (
      <PatientConsumer>
        {({...patientProps}: PatientConsumerRenderProps) => {
          return <Component {...props} {...patientProps} />
        }}
      </PatientConsumer>
  );
};
